import { Typography } from '@workspace/ui/components/Typography';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import { useGlobal } from '~/hooks/useGlobal';
import type { BreadcrumbsItem } from '~/stores/CatalogStore';

import MobileHeaderLayout from '../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../Main/MobileHeader/MobileHeaderTitle';

const LEGALS_NAV = [
  { link: 'general', title: 'Общие положения' },
  { link: 'terms', title: '1. Термины и определения' },
  { link: 'registration', title: '2. Регистрация в программе' },
  { link: 'accrual', title: '3. Начисление бонусов' },
  { link: 'accrual-rules', title: '4. Правила начисления бонусов' },
  { link: 'redemption', title: '5. Списание бонусов' },
  { link: 'limits', title: '6. Ограничения по списанию' },
  { link: 'returns', title: '7. Возврат товаров и бонусов' },
  { link: 'blocking', title: '8. Блокировка карты ПЛ' },
  { link: 'other', title: '9. Иные условия' },
  { link: 'disputes', title: '10. Разрешение споров' },
  { link: 'certificates', title: '11. Подарочные сертификаты' },
  { link: 'certificate-usage', title: '12. Использование сертификата' },
  { link: 'notices', title: '13. Компания уведомляет' },
  { link: 'personal-data', title: '14. Персональные данные' },
  { link: 'final', title: '15. Заключительные положения' },
];

const linkClass = 'text-text-link underline hover:text-text-link-hover';

interface LegalsMCosmeticProps {
  handleLink: (anchor: string) => void;
}

export const LegalsMCosmetic: React.FC<LegalsMCosmeticProps> = ({
  handleLink,
}) => {
  const { t } = useTranslation();
  const { isTablet } = useGlobal();
  const { state } = useLocation();

  const source = state?.source || 'home';

  useEffect(() => {
    setTimeout(() => {
      const anchor = window.location.hash.slice(1);
      handleLink(anchor);
    }, 0);
  }, []);

  return (
    <>
      <MobileHeaderLayout content={<MobileHeaderTitle text={t('legals')} />} />
      <div className="content-layout">
        <div className="mx-auto w-full max-w-(--layout-max-width) pb-24">
          {!isTablet && (
            <>
              <Breadcrumbs
                items={
                  [
                    { text: t('home'), link: '/' },
                    source === 'cabinet' && {
                      text: t('cabinet'),
                      link: '/cabinet',
                    },
                    { text: t('termsOfService'), link: '/legals' },
                  ].filter(Boolean) as BreadcrumbsItem[]
                }
              />
              <Typography.Header200Semibold className="text-text-primary">
                {t('termsOfService')}
              </Typography.Header200Semibold>
            </>
          )}

          <div className="mt-8 flex flex-col gap-14 md:grid md:grid-cols-[286px_1fr] md:gap-10">
            <div className="md:self-start">
              <ul className="flex flex-col">
                {LEGALS_NAV.map((i) => (
                  <li
                    className="w-full border-stroke-tertiary border-b py-2"
                    key={i.link}
                  >
                    <Typography.Text300Medium
                      as="a"
                      href={`#${i.link}`}
                      className="inline-block w-full text-text-primary hover:underline"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        handleLink(i.link);
                      }}
                    >
                      {i.title}
                    </Typography.Text300Medium>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-20 text-text-primary">
              <section id="general">
                <Typography.Header150Semibold className="mb-6">
                  ПУБЛИЧНАЯ ОФЕРТА № 12.2
                </Typography.Header150Semibold>
                <Typography.Text300SemiboldHigh>
                  Дата редакции – 11.03.2026
                </Typography.Text300SemiboldHigh>
                <Typography.Text300SemiboldHigh className="mt-2">
                  УСЛОВИЯ ПРОГРАММЫ ЛОЯЛЬНОСТИ «M Cosmetic»
                </Typography.Text300SemiboldHigh>
                <Typography.Text300RegularHigh className="mt-7">
                  Данный документ, на основании ст.367 Гражданского кодекса
                  Республики Узбекистан (далее по тексту: ГК РУз) является
                  официальным предложением (публичной офертой).
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  ИП ООО «SFERA VOSTOK» (ИНН: 309167892), именуемое в дальнейшем
                  «Компания», в адрес физического лица, юридического лица,
                  индивидуального предпринимателя, именуемого в дальнейшем
                  «Участник», и содержит, в соответствии со ст.21 Закона РУз «О
                  рекламе» от 07.06.2022 г. № ЗРУ-776 (далее по тексту: Закон №
                  ЗРУ-776) все существенные условия по проведению стимулирующей
                  акции Компании в сфере привлечения клиентов по Программе
                  лояльности «M Cosmetic» (далее – «Программа»).
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  Термин «Участник» применительно к юридическому лицу означает
                  само юридическое лицо, а также его заявленных лиц,
                  направленных им в любой из магазинов «M Cosmetic», для
                  приобретения любого товара в указанном магазине в период
                  проведения со стороны Компании Акции.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  В соответствии с частью 4 статьи 370 Гражданского кодекса
                  Республики Узбекистан совершение лицом, получившим оферту,
                  оплаты товара в любом из магазинов «M Cosmetic» (точные адреса
                  магазинов расположены по адресу:{' '}
                  <a
                    href="https://mcosmetic.uz/stores"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://mcosmetic.uz/stores
                  </a>{' '}
                  и являются неотъемлемым приложением к настоящей Публичной
                  оферте) на минимальную сумму в размере 100 сумов, и получение
                  в магазине кассового чека с «QR code», считается акцептом и
                  равносильно заключению договора с Компанией. Срок на акцепт –
                  не ограничен. Программа действует во всех магазинах «M
                  Cosmetic».
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  В связи с вышеизложенным, внимательно прочитайте текст
                  настоящей Публичной оферты и ознакомьтесь с адресами магазинов
                  «M Cosmetic», а также условиями участия. Если Вы не согласны с
                  каким-либо пунктом оферты, Компания предлагает Вам отказаться
                  от покупки любого товара в магазине «M Cosmetic» либо не
                  осуществлять регистрацию в Программе.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  Лицо, принявшее (акцептовавшее) настоящую Публичную Оферту,
                  тем самым подтверждает, что имеет все полномочия заключить
                  Публичную оферту в соответствии с действующим
                  законодательством и несет полную самостоятельную
                  ответственность. Публичная оферта может быть изменена
                  Компанией в любое время без какого-либо специального
                  уведомления об этом Участника. Новая редакция Публичной Оферты
                  вступает в силу с момента ее размещения на сайте Компании,
                  расположенного по адресу{' '}
                  <a
                    href="https://mcosmetic.uz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://mcosmetic.uz
                  </a>
                  , если Компанией прямо не указано иное. Регулярное
                  ознакомление с действующей редакцией Публичной Оферты является
                  обязанностью Участника. Действующая редакция настоящей
                  Публичной Оферты доступна по адресу{' '}
                  <a
                    href="https://mcosmetic.uz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://mcosmetic.uz
                  </a>{' '}
                  (далее по тексту: &ldquo;Сайт&rdquo;), а также в других
                  источниках по усмотрению Компании.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  Основной своей целью настоящей Публичной Оферты является
                  проведение стимулирующей акции для повышения уровня
                  лояльности, привлечение покупателей, увеличение объема продаж.
                  Срок действия Программы неограничен и действует с момента ее
                  запуска и до полной ее отмены по решению Компании.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  В случае приостановления или досрочного прекращения проведения
                  стимулирующей акции «Программа лояльности магазинов «M
                  Cosmetic»» Компания обязуется публично уведомить об этом
                  действующих и потенциальных Участников способами, которыми
                  информация о Программе была доведена до Участника, а именно
                  путем размещения на сайте{' '}
                  <a
                    href="https://mcosmetic.uz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://mcosmetic.uz
                  </a>
                  .
                </Typography.Text300RegularHigh>
              </section>

              <section id="terms">
                <Typography.Header150Semibold className="mb-6">
                  1.ТЕРМИНЫ И ОПРЕДЕЛЕНИЯ
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  1.1. Для целей настоящей Публичной оферты используются
                  следующие термины, в указанном ниже значении:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Акция</strong> – мероприятие, рассчитанное на
                  определенный период времени и/или географию действия и/или
                  перечень Участников, целью которого является формирование и
                  повышение лояльности Участников к Программе. Инициатором
                  организации и проведения Акции выступает Компания при
                  возможном участии Партнеров.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Анкета Участника</strong> – информация о Клиенте,
                  желающем стать Участником Программы, вносимая Клиентом либо
                  сообщаемая Клиентом при регистрации в Программе в порядке,
                  предусмотренном Публичной офертой.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Бонусы</strong> – бонусные единицы, начисляемые
                  Компанией на Бонусный счет Карты Участника за приобретение
                  товаров или услуг у Компании или Партнеров Программы в
                  соответствии с настоящей Публичной офертой или Правилами
                  Акции. Сумма начисленных Бонусов может быть использована
                  Участником для получения скидки на товары и/или услуги,
                  приобретаемые у Компании или Партнеров, а также для получения
                  прочих Бонусов (далее – Бонусы). 1 Бонус равен 1 сумму.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Базовый бонус</strong> – Бонус доступный всем
                  владельцам карт лояльности (далее – &ldquo;Базовый
                  бонус&rdquo;), определяется Компанией согласно Публичной
                  оферте, и может меняться по желанию Компании.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Welcome бонус</strong> – Бонус доступный всем новым
                  владельцам карт лояльности, определяется, начисляется
                  Компанией и может меняться по желанию компании. Сроки
                  предоставления &ldquo;велком бонуса&rdquo; компания определяет
                  сама и оповещает действующих участников способами доступными
                  компании.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Бонус ПЛ</strong> – Накопительный бонус, при
                  достижении определенных условий, установленных Компанией
                  (далее – Бонус ПЛ, Бонус программы лояльности).
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Бонусный счет</strong> – счет Карты Участника в
                  информационной системе Компании, создаваемый или дополняемый в
                  момент прохождения Участником регистрации Карты Участника в
                  Программе в соответствии с настоящей Публичной офертой. На
                  Бонусном счете Участника ведется учет данных по всем
                  Транзакциям, совершаемым Участником с использованием Карты. До
                  начисления на Бонусный счет Участника Бонусы принадлежат
                  Компании. Бонусный счет ведется в Бонусах. Бонусы начисляются
                  на Бонусный счет Карты и списываются с Бонусного счета Карты
                  при приобретении у Компании и/или Партнеров товаров и/или
                  услуг с использованием Карты Участника в соответствии с
                  Публичной офертой. Бонусный счет привязан к номеру мобильного
                  телефона Участника. К одному номеру мобильного телефона в
                  Программе может быть привязан только один Бонусный счет.
                  Участник может изменить номер мобильного телефона только по
                  звонку на номер Горячей линии +998 71-203-00-20.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Накопительная</strong> – Карта Участника Программы,
                  оформленная им самостоятельно или полученная Участником в
                  рамках определенной акции, которая является средством
                  идентификации Участника при обслуживании покупателя и
                  необходимая для начисления и списания Бонусов за совершенные
                  Участником Покупки или иные действия, назначенные Компанией.
                  Карта позволяет Участнику получать Привилегии (списывать,
                  начислять Бонусы, получать скидки или Бонусы) в магазинах «M
                  Cosmetic», а также у Партнеров Программы в соответствии с
                  условиями настоящей Публичной оферты или Правил Акции.
                </Typography.Text300RegularHigh>
                <Typography.Text300SemiboldHigh className="mt-4">
                  Типы Накопительной:
                </Typography.Text300SemiboldHigh>
                <Typography.Text300RegularHigh className="mt-2">
                  <strong>Пластиковая Карта «M Cosmetic»</strong> – пластиковая
                  Карта, оформляемая на кассах магазинов «M Cosmetic».
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-2">
                  <strong>Виртуальная карта &ldquo;M Cosmetic&rdquo;</strong> –
                  доступна всем новым клиентам, оформляется в мобильном
                  приложении или при регистрации на сайте и привязывается к
                  номеру клиента (Клиенту доступна одна активная виртуальная
                  карта)
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Защитный код</strong> – защитный трехзначный код,
                  указанный на обратной стороне пластиковой Карты магазинов «M
                  Cosmetic». Необходим для списания Бонусов.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>ПЛ Сертификат</strong> - далее &ldquo;Подарочный
                  сертификат&rdquo;, разного суммового номинала которая компания
                  устанавливает по собственному желанию. Сертификат является
                  товарной единицей и облагается НДС. Клиент может приобрести
                  сертификат самостоятельно и права воспользоваться им на
                  следующий день или же подарить его. ПЛ Сертификат является
                  обоснование того что клиент приобрел его и соглашается с
                  условиями данной оферты.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  Покупатель может приобрести сертификат самостоятельно,
                  покупатель, который купил сертификат может воспользоваться им
                  сразу после покупки. Проданный сертификат может быть
                  использован любым лицом, предъявившим сертификат у кассы.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>
                    Горячая линия магазинов «M Cosmetic» (также – Горячая линия)
                  </strong>{' '}
                  – центр поддержки Программы, организованный Компанией и
                  осуществляющий информационно-справочное обслуживание
                  Участников по телефону: +998 71-203-00-20.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Клиент</strong> – физическое лицо, являющееся
                  держателем Карты любого вида, но не осуществившее регистрацию
                  в Программе в соответствии с Публичной офертой.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>
                    Личный кабинет магазинов «M Cosmetic» (далее – Личный
                    кабинет или ЛК)
                  </strong>{' '}
                  – сервис, доступный для Участника в сети Интернет по адресу{' '}
                  <a
                    href="https://cab.mcosmetic.uz/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://cab.mcosmetic.uz/
                  </a>
                  , в котором содержится информация об Участнике и данных его
                  профиля, балансе Бонусного счета Карты или общего счета Карт
                  Участника, Персональных предложениях, акциях, скидках и
                  купонах Участника, Истории покупок, совершенных Участником с
                  использованием Карты.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>
                    Мобильное приложение «M Cosmetic» (далее – Мобильное
                    приложение или МП)
                  </strong>{' '}
                  – сервис, доступный для Участника в виде программного
                  обеспечения, устанавливаемого (загружаемого) на мобильное
                  устройство (смартфон, планшет и т.п.) на базе платформ iOS и
                  Android. Мобильное приложение, все его средства
                  индивидуализации, коды и элементы являются собственностью
                  Компании.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  С помощью Личного кабинета и Мобильного приложения Участником
                  могут осуществляться действия, предусмотренные функционалом, в
                  том числе:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh
                  as="ul"
                  className="mt-2 list-disc ps-5"
                >
                  <li>просмотр баланса Бонусного счета;</li>
                  <li>
                    использование штрихкода или QR-кода Виртуальной Карты на
                    кассах магазинов «M Cosmetic» при совершении Транзакций;
                  </li>
                  <li>просмотр истории и детализации Транзакций;</li>
                  <li>просмотр адресов магазинов «M Cosmetic».</li>
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Компания</strong> – ИП ООО «SFERA VOSTOK», юридическое
                  лицо, созданное и осуществляющее свою деятельность в
                  соответствии с законодательством Республики Узбекистан,
                  находящееся по адресу: 100084, Республика Узбекистан,
                  Юнусабадский район, г. Ташкент, Проспект Амира Темура, 88 «А»,
                  обладающее исключительными правами по управлению и развитию
                  Программы магазинов «M Cosmetic» и обеспечивающее
                  предоставление Участникам Программы магазинов «M Cosmetic»
                  Привилегий, предусмотренных настоящей Публичной офертой.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Партнеры</strong> – лица, с которыми у Компании в
                  рамках Программы имеются заключенные соглашения, в рамках
                  которых Участникам предоставляется возможность
                  начисления/списания Бонусов при приобретении товаров и/или
                  услуг этих лиц, а также предоставление иных Привилегий.
                  Условия начисления/списания Бонусов, а также условия
                  предоставления Участникам иных Привилегий, определяются на
                  основании соответствующих соглашений, заключаемых Компанией с
                  Партнерами, условия которых могут изменяться. Актуальный
                  перечень Партнеров и условия предоставления Привилегий
                  размещены на Сайте{' '}
                  <a
                    href="https://mcosmetic.uz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://mcosmetic.uz
                  </a>{' '}
                  и в Мобильном приложении магазинов «M Cosmetic».
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Привилегии</strong> – возможность приобретения товаров
                  и/или услуг у Компании и/или Партнеров с финансовой или
                  нефинансовой выгодой. Привилегии могут предоставляться методом
                  отложенной скидки – начисления Бонусов на Бонусный счет
                  Участника за приобретение товаров и/или услуг у Компании и/или
                  Партнеров и/или третьих лиц, в соответствии с Публичной
                  офертой, и последующего списания Участником накопленных
                  Бонусов при приобретении им товаров и/или услуг у Компании
                  и/или Партнеров в соответствии с Публичной офертой,
                  предоставлением дополнительной скидки и подарков, а также
                  путем предоставления Персональных купонов, начисления Наклеек,
                  которые Участник может обменять на Персональные купоны, в
                  соответствии с правилами, определяемыми Компанией.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Программа</strong> – внедряемые инициативы,
                  направленные на работу с постоянными клиентами магазинов «M
                  Cosmetic».
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>
                    Сайт Программы магазинов «M Cosmetic» (также – Сайт)
                  </strong>{' '}
                  – интернет-сайт Компании, размещенный по адресу:{' '}
                  <a
                    href="https://mcosmetic.uz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://mcosmetic.uz
                  </a>
                  .
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Транзакции</strong> – операции, совершаемые Участником
                  с использованием Карты, которые в соответствии с Публичной
                  офертой являются основанием для начисления Бонусов на Бонусный
                  счет, либо списания Бонусов с Бонусного счета Участника.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Уведомление</strong> – информация, в том числе
                  рекламного содержания, передаваемая Участнику по одному или
                  нескольким средствам (способам) связи, указанным им при
                  регистрации в Программе: в SMS-сообщении, в письме по адресу
                  электронной почты, PUSH-сообщении в Мобильном приложении или
                  Личном кабинете, по почтовому адресу или иными способами.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Участник</strong> – Клиент, который является
                  держателем Карты любого вида, зарегистрированный в Программе в
                  соответствии с настоящей Публичной офертой.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Регистрация</strong> – Процедура занесения данных
                  клиента в базу для дальнейшей обработки в целях настоящей
                  Программы.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Таргет</strong> – формулировка цели, по достижению
                  которой Клиент получает Бонусы ПЛ, помимо Базового бонуса.
                  Таргет достигается путём совершения последовательных действий.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Карта ПЛ M «Cosmetic»</strong> – специальная карта
                  лояльности, доступная к покупке всем покупателям. По данной
                  карте покупатель может приобретать и пользоваться специальными
                  предложениями магазинов &ldquo;M Cosmetic&rdquo;.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Уцененный товар</strong> – Товар по специальной цене,
                  который располагается обычно на предкассовой зоне, товар со
                  сниженной ценой, или с большой скидкой.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  <strong>Промо товар</strong> – товар с повышенной скидкой,
                  применяемой для данного товара в течении определенного
                  ограниченного периода времени, в магазинах помечается желтым
                  ценником с отметкой «nakopitelnaya bo&apos;yicha».
                </Typography.Text300RegularHigh>
              </section>

              <section id="registration">
                <Typography.Header150Semibold className="mb-6">
                  2.РЕГИСТРАЦИЯ В ПРОГРАММЕ ЛОЯЛЬНОСТИ МАГАЗИНОВ «M Cosmetic»
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  2.1. Участником Программы может стать любое лицо, которому на
                  момент прохождения регистрации исполнилось не менее 18
                  (восемнадцати) лет.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.2. Для этого необходимо получить виртуальную карту
                  лояльности, путем скачивания мобильного приложения &ldquo;M
                  Cosmetic&rdquo; или пройти регистрацию на сайте{' '}
                  <a
                    href="https://mcosmetic.uz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://mcosmetic.uz
                  </a>
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.2.1. При регитсрации участник проходит полное анкетирование
                  профиля клиента, указать ФИО, пол, возраст, указывает номер
                  мобильного телефона, проходя смс авторизацию.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.3. В рамках проводимых Акций, может быть предусмотрен иной
                  порядок регистрации в Программе лояльности магазинов «M
                  Cosmetic».
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.4. При регистрации в Программе одним из способов,
                  предусмотренных настоящей с Публичной офертой, Участник также
                  дает согласие Компании, а также лицам, входящим с ним в одну
                  группу лиц по смыслу ст. 5 Закона Республики Узбекистан
                  №ЗРУ-319 «О конкуренции» от 07.01.2012г.:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.4.1. Осуществлять с использованием средств автоматизации
                  и/или без таковых обработку всех персональных данных,
                  указанных Участником при регистрации в Программе, в т.ч. в
                  Анкете, включая сбор, запись, систематизацию, накопление,
                  хранение, уточнение (обновление, изменение), извлечение,
                  использование, передачу, обезличивание, блокирование,
                  удаление, уничтожение данных, а также информации о
                  произведенных Участником покупках, их сумме, способах и
                  средствах их оплаты, в целях, связанных с возможностью
                  предоставления Участнику информации (рекламы), в т.ч. о
                  товарах и/или услугах, о проводимых рекламных акциях, о
                  персональных предложениях, которые потенциально могут
                  предоставлять для Участника интерес, а также в целях сбора,
                  возможностью обеспечения предоставления Участникам Привилегий,
                  предусмотренных Публичной офертой, а также обработки
                  статистической информации и проведения маркетинговых
                  исследований, в том числе с возможностью коммерческого
                  использования результатов данных исследований.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.5. Участник предоставляет Компании в рамках и целях
                  реализации Программы право обрабатывать и передавать
                  аффилированным лицам, Партнерам Программы и третьим лицам, с
                  которыми у Компании заключен соответствующий договор,
                  персональные данные Участника, указанные им для активации
                  Карты Участника Программы лояльности.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.6. Обработка персональных данных Участника осуществляется в
                  соответствии с законодательством РУз. Компания обрабатывает
                  персональные данные Участника только в целях, обозначенных в
                  настоящей Публичной оферте. Компания принимает все необходимые
                  меры для защиты персональных данных Участника от
                  неправомерного доступа, изменения, раскрытия или уничтожения.
                  Компания предоставляет доступ к персональным данным Участника
                  только тем работникам, подрядчикам и Партнерам Компании,
                  которым эта информация необходима для выполнения своих
                  служебных обязанностей. Компания вправе использовать
                  предоставленную Участником информацию, в том числе
                  персональные данные, а также передавать Уполномоченным
                  государственным органам, в целях обеспечения соблюдения
                  требований действующего законодательства РУз. При этом,
                  раскрытие предоставленной Участником информации может быть
                  произведено лишь в соответствии с действующим
                  законодательством РУз по требованию суда, правоохранительных
                  органов, а равно в иных предусмотренных законодательством РУз
                  случаях. Присоединяясь к Программе, Участник выражает свое
                  согласие на обработку его персональных данных Компанией.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.7. Согласие на обработку персональных данных в соответствии
                  с указанными выше условиями предоставляется Участником на срок
                  участия в Программе и может быть отозвано Участником
                  посредством направления Компании письменного заявления
                  почтовым отправлением по адресу: 100084, Республика
                  Узбекистан, Юнусабадский район, г. Ташкент, Проспект Амира
                  Тимура, 88 - &ldquo;А&rdquo;{' '}
                  <a
                    href="https://mcosmetic.uz/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://mcosmetic.uz/
                  </a>
                  , на почту{' '}
                  <a href="mailto:marketing_uz@magnit.ru" className={linkClass}>
                    marketing_uz@magnit.ru
                  </a>
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.8. При регистрации в Программе в соответствии с п. 2.4.
                  Клиент может выразить согласие на получение от Компании
                  Уведомлений, предусмотренных настоящей Публичной офертой.
                  Согласие на получение от Компании Уведомлений выражается
                  Клиентом в момент регистрации в Программе путем проставления
                  соответствующей отметки при регистрации через Личный кабинет
                  на Сайте либо в Мобильном приложении магазинов «M Cosmetic».
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.9. Согласие на получение от Компании Уведомлений,
                  предусмотренных Публичной офертой, может быть выражено
                  Участником в любой момент времени после регистрации в
                  Программе и осуществляется путем проставления соответствующей
                  отметки в Личном кабинете на Сайте или в Мобильном приложении
                  магазинов «M Cosmetic». Участник вправе в любое время
                  отказаться от получения Уведомлений одним из следующих
                  способов:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh
                  as="ul"
                  className="mt-2 list-disc ps-5"
                >
                  <li>
                    в Личном кабинете на Сайте или Мобильном приложении
                    магазинов «M Cosmetic»;
                  </li>
                  <li>обратившись на Горячую линию: +998 71-203-00-20.</li>
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.10. Карта ПЛ «M Cosmetic» является собственностью Компании и
                  подлежит возврату Участником по первому требованию Компании.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.11. Карта ПЛ «M Cosmetic», в том числе Виртуальная Карта не
                  подлежат возврату или обмену, действуют бессрочно до даты
                  прекращения действия Программы согласно настоящей Публичной
                  офертой.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.12. Вход в Мобильное приложение магазинов «M Cosmetic» и
                  Личный кабинет возможен только при нахождении Участника на
                  территории РУз.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  2.13 Работник Компании обязан информировать каждого Участника,
                  о его возможности участия в программе лояльности в
                  соответствии с которой, Участник может получить бонусы в
                  размере 2% от стоимости покупки.
                </Typography.Text300RegularHigh>
              </section>

              <section id="accrual">
                <Typography.Header150Semibold className="mb-6">
                  3.НАЧИСЛЕНИЕ БОНУСОВ
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  3.1. Бонусы начисляются при покупках с использованием Карты.
                  Расчет производится с округлением до ближайшей десятой. Бонусы
                  начисляются за белые и желтые ценники.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  3.2. Начисление при любом способе оплаты: наличными,
                  банковской картой и др.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  3.3. Дополнительные Бонусы начисляются в рамках Акций сверх
                  стандартного количества (п. 4.2).
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  3.4. Участник должен идентифицироваться до момента оплаты.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  3.5. Работник обязан выдавать чек с информацией о начисленных
                  бонусах.
                </Typography.Text300RegularHigh>

                <Typography.Header100Semibold className="mt-7">
                  3.7. Приветственные бонусы (Welcome-бонусы)
                </Typography.Header100Semibold>
                <Typography.Text300RegularHigh className="mt-2">
                  3.7.2. <strong>Бонус за регистрацию:</strong> 15 000 бонусных
                  баллов. Начисление в течение 24 часов. Срок — 30 дней.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  3.7.3. <strong>Купон после первой покупки:</strong> -20% на
                  следующую покупку (до 300 тыс.). Срок — 30 дней.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  3.7.4. <strong>Купон после второй покупки:</strong> -20%
                  аналогично п. 3.7.3 (до 300 тыс.).
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  3.8. <strong>Ограничения:</strong> не более 4 раз в день, не
                  более 9 раз в месяц.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  3.9. <strong>Лимит:</strong> 100 000 бонусных баллов в месяц.
                </Typography.Text300RegularHigh>
              </section>

              <section id="accrual-rules">
                <Typography.Header150Semibold className="mb-6">
                  4.ПРАВИЛА НАЧИСЛЕНИЯ БОНУСОВ ПРИ СОВЕРШЕНИИ ПОКУПОК ТОВАРОВ В
                  МАГАЗИНАХ «M Cosmetic»
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  4.1. Базовый бонус — 2% со всех покупок в сети &ldquo;M
                  Cosmetic&rdquo;.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  4.2. Условия начисления:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh
                  as="ul"
                  className="mt-2 list-disc ps-5"
                >
                  <li>
                    По номеру телефона или карте через мобильное приложение;
                  </li>
                  <li>Только за товары в магазинах «M Cosmetic»;</li>
                  <li>Начисляется наиболее выгодный бонус (не суммируются).</li>
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  4.3. Подтверждающий документ — кассовый чек.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  4.4. При списании Бонусов — начисление только за оплаченную
                  денежными средствами часть.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  4.5. Бонусы не подлежат продаже, передаче, не имеют денежной
                  стоимости.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  4.6. Срок начисления — до 31 календарного дня.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  4.8–4.9. Срок действия Бонусов — 12 месяцев с момента
                  зачисления. Каждый бонус сгорает отдельно, не пролонгируется.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  4.10. При покупке подарочных сертификатов Бонусы не
                  начисляются.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  4.12. Бонусами может воспользоваться только владелец карты.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  4.13. Работникам Компании запрещено использование карты
                  лояльности за/вместо Участника.
                </Typography.Text300RegularHigh>

                <Typography.Header100Semibold className="mt-7">
                  4.1 УСЛОВИЯ ПРИМЕНЕНИЯ СКИДОК В ОПРЕДЕЛЕННЫХ МАГАЗИНАХ
                </Typography.Header100Semibold>
                <Typography.Text300RegularHigh className="mt-2">
                  <strong>a.</strong> Скидки по акциям — только для Участников
                  ПЛ.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-2">
                  <strong>b.</strong> Желтые ценники «nakopitelnaya
                  bo&apos;yicha» — только для Участников ПЛ.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-2">
                  <strong>c.</strong> Города и сроки действия:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh
                  as="ul"
                  className="mt-2 list-disc ps-5"
                >
                  <li>Коканд – 12.03.2026 – 31.12.2026</li>
                  <li>Навои – 12.03.2026 – 31.12.2026</li>
                  <li>Бухара – 12.03.2026 – 31.12.2026</li>
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-2">
                  <strong>d.</strong> Компания может отказаться от применения по
                  ряду товаров.
                </Typography.Text300RegularHigh>
              </section>

              <section id="redemption">
                <Typography.Header150Semibold className="mb-6">
                  5.СПИСАНИЕ БОНУСОВ
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  5.1. По Пластиковой Карте, QR-коду в МП или по номеру телефона
                  с смс-авторизацией.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  5.2. Минимум 100 Бонусов для использования.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  5.3. Участник информирует кассира до закрытия чека.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  5.5. Минимальная оплата покупки — 1000 сум.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  5.6. Не более 3 списаний в день.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  5.8. Списание в любом магазине «M Cosmetic».
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  5.9–5.10. Можно списывать бонусы на уценённый и промо товар.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  5.11. Доступно 100% списание от суммы товара в чеке.
                </Typography.Text300RegularHigh>
              </section>

              <section id="limits">
                <Typography.Header150Semibold className="mb-6">
                  6.ОГРАНИЧЕНИЯ ПО СПИСАНИЮ БОНУСОВ В МАГАЗИНАХ «M Cosmetic»
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  6.1. К списанию — только целое число Бонусов.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  6.2. Скидка не превышает 500 000 сум за раз.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  6.3. Дневной лимит покупок для бонусов — 5 000 000 сум.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  6.4. Не допускается списание с карт без CVV-кода.
                </Typography.Text300RegularHigh>
              </section>

              <section id="returns">
                <Typography.Header150Semibold className="mb-6">
                  7.ВОЗВРАТ ТОВАРОВ И БОНУСОВ, ПРИОБРЕТЕННЫХ С ИСПОЛЬЗОВАНИЕМ
                  КАРТЫ ПЛ
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  7.1. Возврат товаров — по законодательству РУз.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  7.2. При возврате списанные Бонусы возвращаются, начисленные —
                  списываются.
                </Typography.Text300RegularHigh>
              </section>

              <section id="blocking">
                <Typography.Header150Semibold className="mb-6">
                  8.ПОРЯДОК БЛОКИРОВКИ КАРТЫ ПЛ В СЛУЧАЕ ЕЕ УТЕРИ/ПОРЧИ
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  8.1. Заблокировать: +998 71-203-00-20.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  8.2. Для продолжения — новая Карта и регистрация.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  8.3. Списанные до блокировки Бонусы не восстанавливаются.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  8.4. Перенос Бонусов — не более 2 раз в месяц.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  8.5. Для переноса или смены номера — Горячая линия +998
                  71-203-00-20 (нужен доступ к старому номеру для смс-кода).
                </Typography.Text300RegularHigh>
              </section>

              <section id="other">
                <Typography.Header150Semibold className="mb-6">
                  9.ИНЫЕ УСЛОВИЯ
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  9.1. Участник отвечает за корректность данных. Изменения —
                  через ЛК, МП или Горячую линию +998 71-203-00-20.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  9.2. Оферта может быть изменена с публикацией на Сайте.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  9.3. Ответственность за сохранность Карты — на Участнике.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  9.5. Компания вправе прекратить участие, если Участник:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh
                  as="ul"
                  className="mt-2 list-disc ps-5"
                >
                  <li>не соблюдает правила оферты;</li>
                  <li>злоупотребляет Привилегиями;</li>
                  <li>предоставляет недостоверные сведения;</li>
                  <li>не использует Карту более 1 года.</li>
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  9.7. При прекращении участия Бонусы аннулируются.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  9.9. Компания вправе прекратить Программу с уведомлением за 10
                  дней.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  9.11. Правила акций:{' '}
                  <a
                    href="https://mcosmetic.uz/promo_ru"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    https://mcosmetic.uz/promo_ru
                  </a>
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  9.12. Обращения рассматриваются в течение 90 дней.
                </Typography.Text300RegularHigh>
              </section>

              <section id="disputes">
                <Typography.Header150Semibold className="mb-6">
                  10.РАЗРЕШЕНИЕ СПОРОВ ПО ПРОГРАММЕ «M Cosmetic»
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  10.1. Споры разрешаются путем переговоров.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  10.2. При невозможности — в порядке законодательства РУз.
                </Typography.Text300RegularHigh>
              </section>

              <section id="certificates">
                <Typography.Header150Semibold className="mb-6">
                  11. УСЛОВИЯ ПРОГРАММЫ (Подарочные сертификаты)
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  11.1. Номиналы сертификатов:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh
                  as="ul"
                  className="mt-2 list-disc ps-5"
                >
                  <li>100 000 сум</li>
                  <li>150 000 сум</li>
                  <li>200 000 сум</li>
                  <li>300 000 сум</li>
                  <li>500 000 сум</li>
                  <li>1 000 000 сум</li>
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  11.3. Используется только один раз.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  11.4. Не суммируется с другими сертификатами.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  11.6. Можно доплатить недостающую часть. Разница не
                  возвращается.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  11.7. Действует на товары с белым и желтым ценником.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  11.8. Не совместим с другими скидками.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  11.11. Срок действия — 2 месяца.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  11.12. Одноразовый. Неиспользованная сумма сгорает.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  11.13. Обмен на деньги не допускается.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  11.14. За бонусные баллы купить сертификат невозможно.
                </Typography.Text300RegularHigh>
              </section>

              <section id="certificate-usage">
                <Typography.Header150Semibold className="mb-6">
                  12.УСЛОВИЯ ИСПОЛЬЗОВАНИЯ ПОДАРОЧНОГО СЕРТИФИКАТА
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  12.1. Условия активации:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh
                  as="ul"
                  className="mt-2 list-disc ps-5"
                >
                  <li>
                    Срок использования — 2 календарных месяца с момента покупки,
                    только в рабочее время магазинов.
                  </li>
                  <li>
                    Предъявить на кассе. Если стоимость товара меньше номинала,
                    разница не возвращается.
                  </li>
                </Typography.Text300RegularHigh>
              </section>

              <section id="notices">
                <Typography.Header150Semibold className="mb-6">
                  13.КОМПАНИЯ УВЕДОМЛЯЕТ, ЧТО:
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  13.1. Истёкший сертификат утрачивает силу.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  13.2. Не подлежит копированию и обмену на деньги.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  13.3. После использования не восстанавливается.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  13.4. Статусы сертификата:
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh
                  as="ul"
                  className="mt-2 list-disc ps-5"
                >
                  <li>«Истёк срок действия» – срок истёк.</li>
                  <li>«Погашен» – номинал зачтён в счёт оплаты.</li>
                </Typography.Text300RegularHigh>
              </section>

              <section id="personal-data">
                <Typography.Header150Semibold className="mb-6">
                  14.ПОЛИТИКА ОБРАБОТКИ ПЕРСОНАЛЬНЫХ ДАННЫХ
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  14.1. Участник подтверждает согласие на использование
                  информации с маркетинговой целью.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  14.2. Компания гарантирует соблюдение законодательства о
                  защите персональных данных.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  14.5. Отказ от участия: +998 71 203 00 20.
                </Typography.Text300RegularHigh>
              </section>

              <section id="final">
                <Typography.Header150Semibold className="mb-6">
                  15.ЗАКЛЮЧИТЕЛЬНЫЕ ПОЛОЖЕНИЯ
                </Typography.Header150Semibold>
                <Typography.Text300RegularHigh>
                  15.1. Информирование путём размещения Публичной оферты.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  15.2. Правила могут быть изменены Компанией.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  15.3. Компания не отвечает за работу интернет-провайдеров и
                  почтовых служб.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  15.6. Не несет ответственности при форс-мажоре.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  15.7. Неурегулированные отношения — по законодательству РУз.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  15.9. Срок действия Оферты – до окончания Акции.
                </Typography.Text300RegularHigh>
                <Typography.Text300RegularHigh className="mt-4">
                  15.10. Участие в программе лояльности означает согласие с
                  публичной офертой.
                </Typography.Text300RegularHigh>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
